import { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';

/* Last Friday's winners.
 *
 * Public data from the main API, fetched with a plain request rather than through
 * useNetwork(): nobody reading this page is signed in, and the shared client would
 * bring its auth interceptors and a token refresh along for a call that needs
 * neither.
 *
 * `aud` being null means "we do not know what INC was worth then", not "nothing".
 * The week's board is all-or-nothing — one blank line among figures reads as broken
 * rather than incomplete — and the all-time total is withheld while any week is
 * still unpriced, because a partial sum understates it. The two are independent:
 * last Friday can be priced while a week back in July is not.
 * (Admin app → TCG Prizes → Weeks paid.)
 */

const mono = 'ui-monospace, Menlo, Consolas, monospace';
const CYAN = '#38e8ff';
const GOLD = '#ffcf40';
const DIM = '#8ea3b3';

/* One colour per place, lifted from the in-portal leaderboard (Leaderboard.css:
   --lb-gold / --lb-silver / --lb-bronze, then two cyans) so a member who has seen
   the real board recognises this one. Gold is 1st and lives on the big card; these
   are the four beneath it, tapering as they descend — the drop in weight and
   opacity is what makes 2nd read as nearer the top than 5th without anything
   having to say so. */
const PLACES: Record<number, { tint: string; name: string; weight: number; lift: number }> = {
  2: { tint: '#c8d0d8', name: '#eef3f7', weight: 800, lift: 1 },
  3: { tint: '#e79a52', name: '#f3e6dc', weight: 800, lift: 0.94 },
  4: { tint: '#4fd6ee', name: '#dff4fb', weight: 700, lift: 0.88 },
  5: { tint: '#7fb9c9', name: '#cfe4ec', weight: 700, lift: 0.82 }
};
const placeOf = (rank: number) => PLACES[rank] || PLACES[5];

type Winner = { rank: number; username: string; tokens: number; aud: number | null };
type Payload = {
  week: { weekKey: string; paidAt: string } | null;
  winners: Winner[];
  totals: { tokens: number; aud: number | null; weeks: number; players: number };
};

const inc = (n: number) => Math.round(n).toLocaleString('en-AU');
/* Cents on a prize, whole dollars on a running total.
 * A$79.74 is what somebody won and rounding it to A$80 next to an exact INC figure
 * reads as an approximation of a number we know precisely; A$3,244.04 as a headline
 * is just noise. The threshold is where one stops being the other. */
const aud = (n: number) =>
  'A$' +
  n.toLocaleString('en-AU', {
    minimumFractionDigits: n < 1000 ? 2 : 0,
    maximumFractionDigits: n < 1000 ? 2 : 0
  });
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
// '20260821' is the Friday the week STARTED; it is played through to the next Thursday.
const weekRange = (key: string) => {
  if (!/^\d{8}$/.test(key)) return '';
  const start = new Date(Date.UTC(+key.slice(0, 4), +key.slice(4, 6) - 1, +key.slice(6, 8)));
  const end = new Date(start.getTime() + 6 * 86400000);
  const d = (x: Date) => `${x.getUTCDate()} ${MONTHS[x.getUTCMonth()]}`;
  return `${d(start)} – ${d(end)}`;
};

export default function TopWinners() {
  const [data, setData] = useState<Payload | null>(null);

  useEffect(() => {
    let live = true;
    const base = process.env.REACT_APP_API_URL;
    if (!base) return undefined;
    fetch(`${base}/tcg-public/top-winners?limit=5`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: Payload | null) => {
        if (live && d && Array.isArray(d.winners)) setData(d);
      })
      // A marketing page must not show an error box because a leaderboard is
      // briefly unavailable; the section simply is not there.
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  if (!data || !data.winners.length) return null;

  const [champion, ...rest] = data.winners;
  /* Two separate questions now that the board is one week rather than all of them.
   * Last Friday can be fully priced while some week back in July is not, and there
   * is no reason for the older gap to blank out the recent result. The server has
   * already made the week's board all-or-nothing, so this only has to read it. */
  const weekPriced = data.winners.every((w) => w.aud != null);
  const totalPriced = data.totals.aud != null;

  /* pt because this is the FIRST section under the site header — without it the
   * winners strip butts straight up against the nav, which no other page does.
   * Matches the public physical-cards store's { xs: 3, md: 5 }; Contact sits a step
   * tighter at { xs: 2, md: 4 } because its content opens with a heading rather
   * than a kicker. */
  return (
    <Box
      component="section"
      sx={{
        px: { xs: 2.5, md: 3 },
        pt: { xs: 3, md: 5 },
        pb: { xs: 5, md: 7 }
      }}
    >
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        <Typography
          sx={{
            font: `800 11px ${mono}`,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: CYAN,
            textAlign: 'center'
          }}
        >
          Last week&rsquo;s winners
        </Typography>
        <Typography
          component="h2"
          sx={{
            mt: 1.25,
            textAlign: 'center',
            color: '#fff',
            fontWeight: 900,
            fontSize: { xs: 25, md: 34 },
            lineHeight: 1.15,
            textWrap: 'balance'
          }}
        >
          {totalPriced
            ? `${aud(data.totals.aud as number)} of INC paid out so far`
            : `${inc(data.totals.tokens)} INC paid out so far`}
        </Typography>
        <Typography
          sx={{ mt: 1, textAlign: 'center', color: '#c7d6e0', fontSize: 15.5, lineHeight: 1.6 }}
        >
          Every Friday the top of the weekly leaderboard is paid out.
          {data.week && ` These 5 took last week's INC rewards (${weekRange(data.week.weekKey)}).`}
        </Typography>

        <Box
          sx={{
            mt: { xs: 3, md: 4 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.25fr 1fr' },
            gap: { xs: 1.5, md: 2.5 },
            alignItems: 'stretch'
          }}
        >
          {/* the champion, given the room the number deserves */}
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: '18px',
              border: `1px solid ${GOLD}55`,
              background: 'linear-gradient(150deg, rgba(255,207,64,0.14), rgba(56,232,255,0.06))',
              boxShadow: '0 26px 70px -38px rgba(255,207,64,0.6)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <Typography sx={{ font: `800 10px ${mono}`, letterSpacing: '0.28em', color: GOLD }}>
              FIRST PLACE
            </Typography>
            <Typography
              noWrap
              sx={{ mt: 1, color: '#fff', fontWeight: 900, fontSize: { xs: 24, md: 30 } }}
            >
              {champion.username}
            </Typography>
            <Typography
              sx={{
                mt: 1.5,
                font: `900 clamp(30px, 6vw, 46px) ${mono}`,
                color: GOLD,
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums'
              }}
            >
              {inc(champion.tokens)}
              <Box component="span" sx={{ fontSize: 16, color: DIM, ml: 1 }}>
                INC
              </Box>
            </Typography>
            {weekPriced && (
              <Typography sx={{ mt: 0.75, font: `800 17px ${mono}`, color: '#c7d6e0' }}>
                {aud(champion.aud as number)}
              </Typography>
            )}
            <Typography sx={{ mt: 1.5, color: DIM, font: `600 12.5px ${mono}` }}>
              Won the week of {data.week ? weekRange(data.week.weekKey) : ''}
            </Typography>
          </Box>

          {/* the rest */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {rest.map((w) => {
              const p = placeOf(w.rank);
              return (
                <Box
                  key={w.rank}
                  sx={{
                    flex: 1,
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1.5, md: 2 },
                    pl: { xs: 2, md: 2.5 },
                    pr: { xs: 2, md: 2.5 },
                    py: 1.6,
                    borderRadius: '14px',
                    border: `1px solid ${p.tint}2e`,
                    background: `linear-gradient(100deg, ${p.tint}14, rgba(12,18,26,0.92) 46%)`,
                    // the tier's own colour down the leading edge, so the four read
                    // as a descending set rather than four identical cards
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: '3px',
                      background: p.tint,
                      opacity: p.lift
                    }
                  }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      border: `1px solid ${p.tint}55`,
                      background: `${p.tint}1a`,
                      font: `900 13px ${mono}`,
                      color: p.tint
                    }}
                  >
                    {w.rank}
                  </Box>
                  <Typography
                    noWrap
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      color: p.name,
                      fontWeight: p.weight,
                      fontSize: { xs: 15, md: 16 },
                      letterSpacing: '-0.01em'
                    }}
                  >
                    {w.username}
                  </Typography>
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography
                      sx={{
                        font: `900 15.5px ${mono}`,
                        color: p.tint,
                        fontVariantNumeric: 'tabular-nums',
                        lineHeight: 1.15
                      }}
                    >
                      {inc(w.tokens)}
                      <Box component="span" sx={{ fontSize: 10.5, color: DIM, ml: 0.6 }}>
                        INC
                      </Box>
                    </Typography>
                    {weekPriced && (
                      <Typography sx={{ font: `700 12px ${mono}`, color: DIM }}>
                        {aud(w.aud as number)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Typography sx={{ mt: 2, textAlign: 'center', color: DIM, font: `600 12px ${mono}` }}>
          {data.totals.weeks} {data.totals.weeks === 1 ? 'week' : 'weeks'} paid ·{' '}
          {data.totals.players} {data.totals.players === 1 ? 'member' : 'members'} paid so far
          {!totalPriced && ' · the total appears once every week has a price'}
        </Typography>

        {/* The board raises a question — "how do I get on that?" — and the answer
            belongs next to it rather than four screens down. Deliberately quieter
            than the gold offer below: this one tells you the mechanic, that one
            gives you the pack, and two shouting buttons would compete.

            Laid out across rather than down. At the board's own width the copy is
            two lines instead of four and the button sits beside it, so the section
            ends where it should instead of adding another screen of scroll. */}
        <Box
          sx={{
            mt: { xs: 3, md: 4 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            gap: { xs: 2.5, md: 4 },
            textAlign: { xs: 'center', md: 'left' },
            px: { xs: 2.5, md: 4 },
            py: { xs: 3, md: 3.25 },
            borderRadius: '16px',
            border: `1px solid ${CYAN}33`,
            background: 'linear-gradient(100deg, rgba(56,232,255,0.09), rgba(9,14,21,0.6) 62%)'
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ font: `800 10px ${mono}`, letterSpacing: '0.28em', color: CYAN }}>
              HOW YOU GET ON THIS BOARD
            </Typography>
            <Typography
              sx={{ mt: 1, color: '#fff', fontWeight: 800, fontSize: { xs: 18, md: 21 }, lineHeight: 1.3 }}
            >
              Every member plays for it.
            </Typography>
            {/* "sign up and you're playing for it" is only true once they have cards —
                ranked needs a deck you own — so the pack is named as the step it is,
                not skipped over. The offer below carries its own conditions. */}
            <Typography sx={{ mt: 0.75, color: '#c7d6e0', fontSize: 15, lineHeight: 1.55 }}>
              Sign up, claim your free starter pack, and win ranked matches through the
              week. Finish in Friday&rsquo;s top five and your share is paid in INC
              automatically — no entry, no fee.
            </Typography>
          </Box>
          {/* Hands the reader down to the offer rather than off to /sign-up. Someone
              who has just read how the prizes work is one step from wanting the pack,
              not one step from a signup form — and the gold card below is where that
              step is made.

              Still a real anchor, so it is keyboard-reachable and can be opened in a
              new tab; the click only takes over to make the scroll smooth, and backs
              off entirely for anyone who has asked for less motion. */}
          <Button
            component="a"
            href="#starter-pack"
            onClick={(e: React.MouseEvent) => {
              const target = document.getElementById('starter-pack');
              if (!target) return; // no target: let the plain anchor do its job
              e.preventDefault();
              const gentle =
                window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
              const before = document.scrollingElement?.scrollTop ?? 0;
              target.scrollIntoView({ behavior: gentle ? 'auto' : 'smooth', block: 'start' });
              /* Smooth is a nicety; ARRIVING is the requirement. There are contexts
               * where behavior:'smooth' is accepted and then simply never animates —
               * having preventDefault'd, that would leave the button doing nothing at
               * all, which is the worst version of this. So check a beat later and
               * jump outright if nothing moved. If smooth is working it has already
               * moved by now and this does nothing. */
              if (gentle) return;
              window.setTimeout(() => {
                if ((document.scrollingElement?.scrollTop ?? 0) === before) {
                  target.scrollIntoView({ behavior: 'auto', block: 'start' });
                }
              }, 350);
            }}
            sx={{
              flexShrink: 0,
              alignSelf: { xs: 'stretch', md: 'center' },
              px: 3.5,
              py: 1.15,
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 800,
              fontSize: 15,
              whiteSpace: 'nowrap',
              color: CYAN,
              border: `1px solid ${CYAN}66`,
              '&:hover': { background: `${CYAN}14`, borderColor: CYAN }
            }}
          >
            Learn more
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
